import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  TextField,
} from '@mui/material';
import { FileUp, Download } from 'lucide-mui';
import { useTranslation } from 'react-i18next';
import { FeedbackSnackbar, useIframeResizer, GEMINI_MODEL } from './common';
import { config } from '../../../config';

const TextToSRT: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [srtOutput, setSrtOutput] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  useIframeResizer();

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setInputText(content);
    };
    reader.readAsText(file);
  };

  const convertToSRT = async () => {
    if (!inputText.trim()) {
      setError(t('tools.texttosrt.error.noText'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(config.geminiProxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: GEMINI_MODEL,
          contents: [
            {
              parts: [
                {
                  text: `Convert the following text into SRT subtitle format.

Rules:
1. Each subtitle should be numbered sequentially
2. Timestamps should be in format: HH:mm:ss,mmm --> HH:mm:ss,mmm
3. Respect sentence boundaries when possible
4. Don't split words across lines
5. Return ONLY the raw SRT format with no formatting, no code blocks, no markdown, and no additional text
6. Use approximately 4 words per second for timing
7. Maximum 42 characters per line
8. Maximum 2 lines per subtitle
9. Do not add any formatting or symbols around the response

Text to convert:
${inputText.slice(0, 32000)}
`,
                },
              ],
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error(t('tools.texttosrt.error.convertFailed'));
      }

      const data = await response.json();
      const srtContent = data.candidates[0].content.parts[0].text.trim();
      setSrtOutput(srtContent);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('tools.texttosrt.error.generic')
      );
    } finally {
      setIsLoading(false);
    }
  };

  const downloadSRT = () => {
    if (!srtOutput) return;

    const blob = new Blob([srtOutput], { type: 'text/srt' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName
      ? fileName.replace(/\.[^/.]+$/, '.srt')
      : 'subtitles.srt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ mb: 3 }}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".txt,.doc,.docx,.xml,.ttml,.mks,.lrc,.vtt,.sbv"
            style={{ display: 'none' }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<FileUp />}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                width: 400,
                py: 1.5,
                fontSize: '1.1rem',
              }}
            >
              {t('tools.texttosrt.uploadButton')}
            </Button>
          </Box>

          <Typography
            variant="caption"
            align="center"
            component="div"
            sx={{
              mb: 3,
              color: 'text.secondary',
            }}
          >
            {t('tools.texttosrt.supportedFormats')}
          </Typography>

          <Typography
            variant="body1"
            align="center"
            sx={{
              mb: 2,
              color: 'text.secondary',
              fontWeight: 500,
            }}
          >
            {t('tools.texttosrt.orPasteText')}
          </Typography>

          <TextField
            multiline
            rows={8}
            fullWidth
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder={t('tools.texttosrt.textPlaceholder')}
            variant="outlined"
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Button
              variant="contained"
              onClick={convertToSRT}
              disabled={!inputText.trim() || isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : null}
              sx={{
                width: 400,
                py: 1.5,
                fontSize: '1.1rem',
              }}
            >
              {isLoading
                ? t('tools.texttosrt.converting')
                : t('tools.texttosrt.convertButton')}
            </Button>
          </Box>
        </Box>

        {srtOutput && (
          <Box sx={{ mt: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <Typography variant="subtitle1">
                {t('tools.texttosrt.preview')}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={downloadSRT}
                size="small"
              >
                {t('tools.texttosrt.downloadButton')}
              </Button>
            </Box>
            <TextField
              multiline
              rows={8}
              fullWidth
              value={srtOutput}
              InputProps={{ readOnly: true }}
              variant="outlined"
              sx={{
                fontFamily: 'monospace',
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace',
                },
              }}
            />
          </Box>
        )}
      </Paper>

      <FeedbackSnackbar
        open={!!error}
        message={error || t('tools.texttosrt.error.generic')}
        severity="error"
        onClose={() => setError(null)}
      />
    </Box>
  );
};

export default TextToSRT;
